import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Paper,
  Button,
  Modal,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  MonetizationOn as GoldIcon,
  DirectionsCar as CarIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import ResponsiveAppBar from "../components/AppBar";
import { API_BASE_URL } from "../api/config";

const BASE_URL = API_BASE_URL;

const LoanDetailsPage = () => {
  const [goldLoans, setGoldLoans] = useState([]);
  const [vehicleLoans, setVehicleLoans] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [viewType, setViewType] = useState("gold");
  const navigate = useNavigate();

  useEffect(() => {
    fetchLoans();
    fetchVehicleLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/gold-loan/get_all_goldloan`);
      setGoldLoans(response.data || []);
    } catch (error) {
      console.error("Error fetching gold loans:", error);
    }
  };

  const fetchVehicleLoans = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/vehicle-loan/get_all_loans`);
      setVehicleLoans(response.data || []);
    } catch (error) {
      console.error("Error fetching vehicle loans:", error);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].clientX;

      if (touchStartX - touchEndX > 50) {
        navigate("/admin");
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [navigate]);

  // Define columns for DataGrid
  const goldLoanColumns = [
    {
      field: "borrowerName",
      headerName: "Customer Name",
      flex: 1.2,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
          {params.value || "N/A"}
        </Typography>
      )
    },
    { field: "email", headerName: "Email Address", flex: 1.3 },
    { field: "phoneNumber", headerName: "Contact Phone", flex: 1 },
    { field: "address", headerName: "Permanent Address", flex: 1.5 },
    {
      field: "goldWeight",
      headerName: "Gold Weight (g)",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={`${params.value || 0} g`}
          size="small"
          sx={{
            bgcolor: 'rgba(119, 200, 199, 0.15)',
            color: '#0f766e',
            fontWeight: 700,
            borderRadius: 2,
          }}
        />
      )
    },
  ];

  const vehicleColumns = [
    {
      field: "fullName",
      headerName: "Applicant Name",
      flex: 1.2,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
          {params.value || "N/A"}
        </Typography>
      )
    },
    { field: "email", headerName: "Email Address", flex: 1.2 },
    { field: "phoneNumber", headerName: "Phone", flex: 1 },
    { field: "address", headerName: "Address", flex: 1.3 },
    {
      field: "employmentStatus",
      headerName: "Employment",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value || "Employed"}
          size="small"
          sx={{
            bgcolor: 'rgba(147, 96, 151, 0.12)',
            color: '#936097',
            fontWeight: 600,
            borderRadius: 2,
          }}
        />
      )
    },
    { field: "vehicleName", headerName: "Vehicle Brand", flex: 1 },
    { field: "vehicleModel", headerName: "Model", flex: 1 },
    {
      field: "loanAmount",
      headerName: "Loan Amount",
      flex: 1.1,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '0.875rem' }}>
          ₹{params.value ? Number(params.value).toLocaleString() : "N/A"}
        </Typography>
      )
    },
    {
      field: "vehicleImageUrl",
      headerName: "Vehicle Photo",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <Box
            component="img"
            src={params.value}
            alt="Vehicle"
            sx={{
              width: 50,
              height: 34,
              objectFit: 'cover',
              borderRadius: 1.5,
              cursor: "pointer",
              border: '1px solid rgba(119, 200, 199, 0.4)',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.15)',
              }
            }}
            onClick={() => handleImageClick(params.value)}
          />
        ) : (
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>N/A</Typography>
        ),
    },
  ];

  const goldRows = goldLoans.map((row, index) => ({ ...row, id: row._id || index }));
  const vehicleRows = vehicleLoans.map((row, index) => ({ ...row, id: row._id || index }));

  return (
    <>
      <ResponsiveAppBar />
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, md: 5 } }}>
        <Container maxWidth="xl">
          {/* Header Title Section */}
          <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
            <Box>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin")}
                sx={{
                  color: '#936097',
                  fontWeight: 700,
                  p: 0,
                  mb: 1,
                  '&:hover': { bgcolor: 'transparent', color: '#77c8c7' }
                }}
              >
                Back to Admin Dashboard
              </Button>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px' }}>
                Master Loan Details Ledger
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                Comprehensive records of all submitted Gold & Vehicle loan inquiries
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <ToggleButtonGroup
                value={viewType}
                exclusive
                onChange={(event, newValue) => {
                  if (newValue) setViewType(newValue);
                }}
                sx={{
                  bgcolor: '#ffffff',
                  p: 0.5,
                  borderRadius: 3,
                  border: '1px solid rgba(226, 232, 240, 0.9)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: 2.5,
                    px: 3,
                    py: 1,
                    fontWeight: 700,
                    color: '#64748b',
                    display: 'flex',
                    gap: 1,
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      bgcolor: viewType === 'gold' ? 'rgba(119, 200, 199, 0.15)' : 'rgba(147, 96, 151, 0.12)',
                      color: viewType === 'gold' ? '#0f766e' : '#936097',
                      '&:hover': {
                        bgcolor: viewType === 'gold' ? 'rgba(119, 200, 199, 0.2)' : 'rgba(147, 96, 151, 0.18)',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="gold">
                  <GoldIcon sx={{ fontSize: 18 }} />
                  Gold Loans ({goldLoans.length})
                </ToggleButton>
                <ToggleButton value="vehicle">
                  <CarIcon sx={{ fontSize: 18 }} />
                  Vehicle Loans ({vehicleLoans.length})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* DataGrid Container */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              bgcolor: '#ffffff',
              border: '1px solid rgba(226, 232, 240, 0.9)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
              p: 2.5,
            }}
          >
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: viewType === 'gold' ? 'rgba(119, 200, 199, 0.15)' : 'rgba(147, 96, 151, 0.12)',
                    color: viewType === 'gold' ? '#77c8c7' : '#936097',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {viewType === 'gold' ? <GoldIcon /> : <CarIcon />}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  {viewType === "gold" ? "Gold Loan Applications Ledger" : "Vehicle Loan Applications Ledger"}
                </Typography>
              </Box>
              <Chip
                label={`${viewType === 'gold' ? goldRows.length : vehicleRows.length} Records`}
                sx={{
                  bgcolor: '#f1f5f9',
                  color: '#475569',
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Box sx={{ height: 560, width: "100%" }}>
              <DataGrid
                rows={viewType === "gold" ? goldRows : vehicleRows}
                columns={viewType === "gold" ? goldLoanColumns : vehicleColumns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    bgcolor: '#f8fafc',
                    color: '#475569',
                    fontWeight: 700,
                    borderRadius: 2,
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      bgcolor: 'rgba(119, 200, 199, 0.04)',
                    },
                  },
                  '& .MuiDataGrid-cell': {
                    borderColor: '#f1f5f9',
                    color: '#334155',
                  },
                }}
              />
            </Box>
          </Paper>

          {/* Image Preview Modal */}
          <Modal open={!!selectedImage} onClose={handleCloseModal}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "#ffffff",
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                p: 3,
                borderRadius: 4,
                outline: "none",
                maxWidth: 680,
                width: "90%",
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Vehicle Inspection Photo
                </Typography>
              </Box>
              <Box
                component="img"
                src={selectedImage}
                alt="Vehicle Preview"
                sx={{ width: "100%", maxHeight: 450, objectFit: 'contain', borderRadius: 2.5, bgcolor: '#f8fafc' }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
                <Button
                  onClick={handleCloseModal}
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    borderRadius: 2.5,
                  }}
                >
                  Close Preview
                </Button>
              </Box>
            </Box>
          </Modal>
        </Container>
      </Box>
    </>
  );
};

export default LoanDetailsPage;
